import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Clock, BookOpen, Star, Lock, ShoppingCart, Check, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
  const { user, token } = useAuth();
  const { addItem, items } = useCart();
  const navigate = useNavigate();

  // Sample courses data (will be replaced with API data)
  const sampleCourses = [
    {
      id: 'course-1',
      title: 'Emocinio intelekto pagrindai',
      description: 'Išsamus video kursas apie emocinio intelekto ugdymą. Išmoksite atpažinti, suprasti ir valdyti savo emocijas bei geriau suprasti kitus.',
      price: 49.99,
      duration: '5 valandos',
      lessons_count: 10,
      thumbnail: null,
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]',
      features: ['10 video pamokų', '5 val. turinio', 'Prieiga 2 metus', 'Sertifikatas']
    },
    {
      id: 'course-2',
      title: 'Streso valdymo meistras',
      description: 'Praktinis kursas su konkrečiomis technikomis kasdieniam streso valdymui darbe ir asmeniniame gyvenime.',
      price: 39.99,
      duration: '3 valandos',
      lessons_count: 8,
      thumbnail: null,
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]',
      features: ['8 video pamokos', '3 val. turinio', 'Prieiga 2 metus', 'Darbo knyga PDF']
    },
    {
      id: 'course-3',
      title: 'Sąmoningas bendravimas',
      description: 'Kursas apie efektyvų ir sąmoningą bendravimą. Išmoksite klausyti, kalbėti ir spręsti konfliktus.',
      price: 59.99,
      duration: '4 valandos',
      lessons_count: 12,
      thumbnail: null,
      color: 'bg-[#5B9BD5]',
      lightColor: 'bg-[#E3F2FD]',
      features: ['12 video pamokų', '4 val. turinio', 'Prieiga 2 metus', 'Praktinės užduotys']
    }
  ];

  useEffect(() => {
    loadCourses();
    if (user && token) {
      loadPurchasedCourses();
    }
  }, [user, token]);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses`);
      if (response.data && response.data.length > 0) {
        setCourses(response.data);
      } else {
        setCourses(sampleCourses);
      }
    } catch (error) {
      console.log('Using sample courses');
      setCourses(sampleCourses);
    } finally {
      setLoading(false);
    }
  };

  const loadPurchasedCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/my-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchasedCourseIds(response.data.map(c => c.course_id));
    } catch (error) {
      console.log('Could not load purchased courses');
    }
  };

  const handleAddToCart = (course) => {
    addItem({
      id: course.id,
      title: course.title,
      price: course.price,
      quantity: 1,
      type: 'course'
    });
    toast.success(`"${course.title}" pridėta į krepšelį`);
  };

  const isInCart = (courseId) => {
    return items.some(item => item.id === courseId);
  };

  const isPurchased = (courseId) => {
    return purchasedCourseIds.includes(courseId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="courses-page" className="min-h-screen bg-[#FAFAF8]">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC4] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <div className="inline-flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Play className="w-4 h-4" />
            <span>Video kursai</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold text-white mb-6">
            Mokykitės savo tempu
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Profesionalūs video kursai apie emocinį intelektą, streso valdymą ir asmeninį augimą. 
            Žiūrėkite bet kada, bet kur – 2 metus.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center mb-2">
                <Play className="w-6 h-6 text-[#4CAF50]" />
              </div>
              <span className="text-sm font-medium text-[#2D3748]">HD kokybė</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#FFF3E0] rounded-full flex items-center justify-center mb-2">
                <Clock className="w-6 h-6 text-[#FF9F43]" />
              </div>
              <span className="text-sm font-medium text-[#2D3748]">Prieiga 2 metus</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#E3F2FD] rounded-full flex items-center justify-center mb-2">
                <BookOpen className="w-6 h-6 text-[#5B9BD5]" />
              </div>
              <span className="text-sm font-medium text-[#2D3748]">Papildoma medžiaga</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-[#F3E5F5] rounded-full flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-[#9B59B6]" />
              </div>
              <span className="text-sm font-medium text-[#2D3748]">Visoms įrenginiams</span>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => {
              const inCart = isInCart(course.id);
              const purchased = isPurchased(course.id);

              return (
                <div
                  key={course.id}
                  data-testid={`course-card-${course.id}`}
                  className="bg-white rounded-3xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  {/* Thumbnail */}
                  <div className={`${course.lightColor} h-48 flex items-center justify-center relative`}>
                    <div className={`w-20 h-20 ${course.color} rounded-full flex items-center justify-center`}>
                      <Play className="w-10 h-10 text-white ml-1" />
                    </div>
                    {purchased && (
                      <div className="absolute top-4 right-4 bg-[#4CAF50] text-white px-3 py-1 rounded-full text-xs font-medium">
                        Nusipirkta
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-['Poppins'] font-bold text-[#2D3748] mb-2">
                      {course.title}
                    </h3>
                    <p className="text-[#718096] text-sm mb-4 line-clamp-2">
                      {course.description}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 mb-4 text-sm text-[#718096]">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {course.duration}
                      </div>
                      <div className="flex items-center">
                        <BookOpen className="w-4 h-4 mr-1" />
                        {course.lessons_count} pamokų
                      </div>
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {course.features?.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-slate-100 text-[#718096] px-2 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Price & Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-2xl font-bold text-[#2D3748]">
                        €{course.price.toFixed(2)}
                      </span>
                      
                      {purchased ? (
                        <Link
                          to={`/kursai/${course.id}/ziureti`}
                          className="flex items-center space-x-2 bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-5 py-2.5 font-medium transition-colors"
                        >
                          <Play className="w-4 h-4" />
                          <span>Žiūrėti</span>
                        </Link>
                      ) : inCart ? (
                        <Link
                          to="/krepselis"
                          className="flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] rounded-full px-5 py-2.5 font-medium"
                        >
                          <Check className="w-4 h-4" />
                          <span>Krepšelyje</span>
                        </Link>
                      ) : (
                        <button
                          onClick={() => handleAddToCart(course)}
                          className="flex items-center space-x-2 bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-5 py-2.5 font-medium transition-colors"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          <span>Pirkti</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {courses.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-10 h-10 text-[#5B9BD5]" />
              </div>
              <h3 className="text-xl font-semibold text-[#2D3748] mb-2">
                Kursai ruošiami
              </h3>
              <p className="text-[#718096]">
                Netrukus čia atsiras video kursai. Sekite naujienas!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl font-['Poppins'] font-bold text-white mb-4">
            Norite individualių mokymų?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir sukursime programą pagal jūsų poreikius
          </p>
          <Link
            to="/kontaktai"
            className="inline-flex items-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Susisiekti
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
